import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Database, SearchIcon } from "lucide-react";
import type { IStoreComparison } from "src/@types/campaign-comparison";

interface StoreRawComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store: IStoreComparison | null;
}

const renderValue = (value: string | number | null) =>
  value === null || value === undefined || value === "" ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    String(value)
  );

/**
 * Comparativo REAL/CRU: todas as colunas do banco (retaguarda vs loja), linha a
 * linha, com busca por coluna e filtro de "somente diferenças".
 */
export function StoreRawComparisonDialog({
  open,
  onOpenChange,
  store,
}: StoreRawComparisonDialogProps) {
  const [search, setSearch] = useState("");
  const [onlyDiffs, setOnlyDiffs] = useState(false);

  const rows = useMemo(() => store?.rawComparison ?? [], [store]);
  const diffCount = useMemo(
    () => rows.filter((r) => r.differs).length,
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyDiffs && !r.differs) return false;
      if (!q) return true;
      return (
        r.column.toLowerCase().includes(q) ||
        String(r.varValue ?? "").toLowerCase().includes(q) ||
        String(r.storeValue ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, onlyDiffs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="max-w-4xl max-h-[88vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Database className="h-5 w-5 text-primary" />
            Comparativo completo (todas as colunas)
          </DialogTitle>
          {store && (
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>
                Loja #{store.storeCode}
                {store.storeName ? ` - ${store.storeName}` : ""}
              </span>
              <Badge variant="secondary">{rows.length} colunas</Badge>
              {diffCount > 0 && (
                <Badge className="bg-yellow-500 text-yellow-950 hover:bg-yellow-600">
                  {diffCount} diferença(s)
                </Badge>
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Toolbar: busca + somente diferenças */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar por coluna ou valor..."
              className="h-9 pl-8"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={onlyDiffs}
              onCheckedChange={(v) => setOnlyDiffs(!!v)}
            />
            Somente diferenças
          </label>
          {(search || onlyDiffs) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setOnlyDiffs(false);
              }}
            >
              Limpar
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[60vh] rounded-md border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead className="w-1/3">Coluna</TableHead>
                <TableHead>Retaguarda</TableHead>
                <TableHead>Loja</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-20 text-center text-muted-foreground"
                  >
                    Nenhuma coluna corresponde ao filtro
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow
                    key={row.column}
                    className={cn(
                      row.differs && "bg-yellow-50 dark:bg-yellow-950/20"
                    )}
                  >
                    <TableCell className="font-mono text-xs font-medium">
                      {row.column}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {renderValue(row.varValue)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "font-mono text-xs",
                        row.differs &&
                          "font-semibold text-yellow-700 dark:text-yellow-400"
                      )}
                    >
                      {renderValue(row.storeValue)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
